import { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    MenuItem,
    IconButton,
    FormControl,
    Drawer,
    InputLabel,
    Select,
    Tooltip,
    Fade,
    LinearProgress,
    Grid,
} from '@mui/material';
import { SCUIAutoCompleteInput, SCUIDateRangerV2 } from '../../../atoms';
import { SCUIQuickDateSelector } from '../../../molecules';
import { useTranslation } from 'react-i18next';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import SearchIcon from '@mui/icons-material/Search';
import CommentFilterV2 from '../../../../pages/ProductComments/CommentFilter/CommentFilterV2';
import { CommentQuery } from '../../../../services/query';
import TimeHelper from '../../../../utils/time';
import { useLookupLists } from '../../../../pages/ProductComments/CommentFilter/helper';
import SCUIDownloadButton from '../../../atoms/SCUIDownloadButton/SCUIDownloadButton';
import Swal from 'sweetalert2';

function ProductCommentFilterComponentV2({
    tagNames,
    params,
    refetchGridData,
    setParams,
    pageCount,
    createQueryParams,
}: any) {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [endDate, setEndDate] = useState(params.endDate ? params.endDate : '');
    const [createdDate, setCreatedDate] = useState(params.createDate ? params.createDate : '');
    const [selectedStatus, setSelectedStatus] = useState(params.commentStatus[0].id);
    const [selectedGrievance, setSelectedGrievance] = useState(params.grievanceStatus[0].id);
    const [specialCode, setSpecialCode] = useState(params.specialCode);
    const [excelData, setExcelData] = useState([]);

    useEffect(() => {
        setCreatedDate(params.createDate || '');
        setEndDate(params.endDate || '');
    }, [params.createDate, params.endDate]);

    const { t } = useTranslation();

    const {
        grievanceStatusLookupList,
        enumCommentStatusFlowStatusTrName,
        grievanceStatusSelectBoxLookupList,
        commentStatusLookupList,
    } = useLookupLists();

    const toggleDrawer = (open: any) => (event: any) => {
        if (event?.type === 'keydown' && (event?.key === 'Tab' || event?.key === 'Shift')) {
            return;
        }
        setIsDrawerOpen(open);
    };

    // Helper function to format image URLs
    const formatImageUrls = (images: any[]) => {
        if (!images || images.length === 0) return '-';
        return images.map((img: any) => img.imageUrl || '-').join(', ');
    };

    // Helper function to transform comment item to excel format
    const transformCommentToExcel = (item: any) => ({
        comment_Excel_commentId: item.id || '-',
        comment_Excel_brandName: item.brandName !== null ? item.brandName : '-',
        comment_Excel_specialCode: item.specialCode || '-',
        comment_Excel_colorCode: item.colorCode || '-',
        comment_Excel_mail: item.userEmail || '-',
        comment_Excel_grievanceStatus: grievanceStatusLookupList[item.grievanceStatus]?.value || '-',
        comment_Excel_userOperatorName: item?.userOperatorName || '-',
        comment_Excel_commentDate: TimeHelper.formatDateHour(item.commentDate) || '-',
        comment_Excel_tagname: item.tagname || '-',
        comment_Excel_isAgent: item.isAgent == null ? true : item.isAgent,
        comment_Excel_threshold: item?.threshold !== null ? item.threshold.toString() : '-',
        comment_Excel_labelProp: item.labelProp !== null ? item.labelProp.toString() : '-',
        comment_Excel_commentImages: item.images.length > 0 ? 'Var' : t('general.none'),
        comment_Excel_commentStatus: enumCommentStatusFlowStatusTrName[item.status],
        comment_Excel_shipmentStateCode: item.shipmentStateCode || '-',
        comment_Excel_starScore: item.starScore || '-',
        comment_Excel_commentText: item.commentText || '-',
        comment_Excel_changeOn: TimeHelper.formatDateHour(item.changeOn) || '-',
        comment_Excel_customerId: item.customerId || '-',
        comment_Excel_grievanceStatusText: item.grievanceStatusText || '-',
        comment_Excel_photoimageUrl: formatImageUrls(item.images),
    });

    const buildExcel = () => {
        if (params.commentStatus?.[0]?.id !== 1 && (!params.createDate || !params.endDate)) {
            Swal.fire(t('general.error') || 'Hata', t('productCommentList.pleaseEnterDateRange') || 'Lütfen tarih aralığı giriniz.', 'error');
            return Promise.resolve([]);
        }

        const param: any = createQueryParams(params, 0, 20, pageCount);
        return CommentQuery.GetCommentDataV2(param).then((res: any) => {
            if (!res) return [];

            const parseData = res.data.map(transformCommentToExcel);
            setExcelData(parseData);
            return parseData;
        });
    };

    const _handlChangeDate = (event: any) => {
        setCreatedDate(event[0]);
        setEndDate(event[1]);
        setParams((prevParams: any) => ({
            ...prevParams,
            createDate: event[0],
            endDate: event[1],
        }));
    };

    const handleQuickDateRangeSelect = (startDate: string, endDate: string) => {
        _handlChangeDate([startDate, endDate]);
    };

    const _handleChangeTag = (_: any, newValue: any) => {
        setParams((prevParams: any) => ({
            ...prevParams,
            tagName: newValue,
        }));
    };

    const handleStatusChange = (event: any) => {
        setSelectedStatus(event.target.value as string);
        setParams((prevParams: any) => ({
            ...prevParams,
            commentStatus: [{ id: event.target.value }],
        }));
    };

    const handleGrievanceChange = (event: any) => {
        setSelectedGrievance(event.target.value as string);
        setParams((prevParams: any) => ({
            ...prevParams,
            grievanceStatus: [{ id: event.target.value, value: event.target.name }],
        }));
    };

    return (
        <>
            <Fade style={{ marginBottom: '5rem' }} in={false} unmountOnExit>
                <LinearProgress />
            </Fade>
            <Drawer
                anchor="right"
                open={isDrawerOpen}
                onClose={toggleDrawer(false)}
                sx={{
                    '& .MuiDrawer-paper': {
                        padding: '16px',
                        width: 'min(1040px, 100vw)',
                        maxWidth: 1040,
                    },
                }}
            >
                <CommentFilterV2
                    onClose={toggleDrawer(false)}
                    endDate={endDate}
                    setEndDate={setEndDate}
                    createdDate={createdDate}
                    setCreatedDate={setCreatedDate}
                    selectedStatus={selectedStatus}
                    setSelectedStatus={setSelectedStatus}
                    selectedGrievance={selectedGrievance}
                    setSelectedGrievance={setSelectedGrievance}
                    specialCode={specialCode}
                    setSpecialCode={setSpecialCode}
                    tagNames={tagNames}
                    setParams={setParams}
                    refetchGridData={refetchGridData}
                    params={params}
                    hasPhoto={params.hasPhoto}
                />
            </Drawer>
            <Box sx={{ width: '100%' }}>
                <Grid container spacing={2}>
                    <Grid
                        size={{
                            xs: 12,
                            sm: 6,
                            md: 3,
                        }}
                    >
                        <FormControl fullWidth>
                            <SCUIAutoCompleteInput
                                defValue={params.tagName}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        height: '35px',
                                        padding: '5px 0 0 8px',
                                    },
                                    '& .MuiInputBase-root.Mui-focused': {
                                        padding: '8px',
                                    },
                                    '& .MuiChip-label': {
                                        overflow: 'hidden',
                                    },
                                    '& .MuiButtonBase-root.MuiChip-root': {
                                        maxWidth: 'calc(100% - 24px)',
                                    },
                                    '& .MuiAutocomplete-tag': {
                                        paddingTop: '2px',
                                    },
                                }}
                                list={tagNames}
                                onChange={_handleChangeTag}
                                limitTags={1}
                                txtPlaceHolder={t('general.tag')}
                                searchValue="keyWord"
                            />
                        </FormControl>
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            sm: 6,
                            md: 3,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SCUIDateRangerV2
                                fromDate={createdDate}
                                toDate={endDate}
                                setDateRange={_handlChangeDate}
                                dateFormat="YYYY-MM-DD"
                                containerStyling={{ width: '100%' }}
                            />
                            <SCUIQuickDateSelector
                                onDateRangeSelect={handleQuickDateRangeSelect}
                                translationNamespace="comments"
                            />
                        </Box>
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            sm: 6,
                            md: 3,
                        }}
                    >
                        <FormControl variant="outlined" size="small" fullWidth>
                            <InputLabel id="status-select-label">{t('productCommentList.selectStatus')}</InputLabel>
                            <Select
                                labelId="status-select-label"
                                value={selectedStatus}
                                onChange={handleStatusChange}
                                label={t('productCommentList.selectStatus')}
                            >
                                {commentStatusLookupList.map((status: any) => (
                                    <MenuItem value={status.id} key={status.id}>
                                        {status.value}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            sm: 6,
                            md: 3,
                        }}
                    >
                        <FormControl variant="outlined" size="small" fullWidth>
                            <InputLabel id="grievance-select-label">{t('productCommentList.victimization')}</InputLabel>
                            <Select
                                labelId="grievance-select-label"
                                value={selectedGrievance}
                                onChange={handleGrievanceChange}
                                label={t('productCommentList.victimization')}
                            >
                                {grievanceStatusSelectBoxLookupList.map((grievance: any) => (
                                    <MenuItem value={grievance.id} key={grievance.id}>
                                        {grievance.value}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            sm: 6,
                            md: 3,
                        }}
                    >
                        <TextField
                            label={t('productCommentList.specialCode')}
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={params.specialCode}
                            onChange={(event: any) => {
                                const newSpecialCode = event.target.value.trim();
                                setParams((prevParams: any) => ({
                                    ...prevParams,
                                    specialCode: newSpecialCode,
                                }));
                            }}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    refetchGridData(0);
                                }
                            }}
                        />
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            sm: 6,
                            md: 3,
                        }}
                    >
                        <TextField
                            label={t('productCommentList.colorCode')}
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={params.colorCode}
                            onChange={(event: any) => {
                                const newColorCode = event.target.value.trim();
                                setParams((prevParams: any) => ({
                                    ...prevParams,
                                    colorCode: newColorCode,
                                }));
                            }}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    refetchGridData(0);
                                }
                            }}
                        />
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            sm: 6,
                            md: 3,
                        }}
                    >
                        <TextField
                            label={t('productCommentList.commentId')}
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={params.id}
                            onChange={(event: any) => {
                                const id = event.target.value.trim();
                                setParams((prevParams: any) => ({
                                    ...prevParams,
                                    id: id,
                                }));
                            }}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    refetchGridData(0);
                                }
                            }}
                        />
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            sm: 6,
                            md: 3,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                            <Tooltip title={t('general.filter')}>
                                <IconButton color="primary">
                                    <SearchIcon style={{ fontSize: 30 }} onClick={() => refetchGridData(0)} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={t('general.advanceFilter')}>
                                <IconButton color="default">
                                    <FilterAltOutlinedIcon
                                        onClick={() => setIsDrawerOpen(true)}
                                        style={{ fontSize: 30 }}
                                    />
                                </IconButton>
                            </Tooltip>
                            <SCUIDownloadButton
                                position="right"
                                excelData={{
                                    excelDownloadData: excelData,
                                    excelDownloadFolderName: t('excel.commentList'),
                                    columnConfig: {
                                        comment_Excel_commentText: { maxWidth: 85, wrapText: true },
                                        comment_Excel_photoimageUrl: { maxWidth: 85, wrapText: true },
                                    },
                                }}
                                onclick={buildExcel}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
}

export default ProductCommentFilterComponentV2;
